import React from 'react';
import Icon from '../../../components/AppIcon';

const AboutUsSection = () => {
  const values = [
    {
      icon: 'Sparkles',
      title: 'Simplicity',
      description: 'Clear tools without unnecessary complexity'
    },
    {
      icon: 'Eye',
      title: 'Transparency',
      description: 'No hidden practices or confusing features'
    },
    {
      icon: 'Shield',
      title: 'Privacy-First Design',
      description: 'Your data stays yours'
    },
    {
      icon: 'Users',
      title: 'User Empowerment',
      description: 'Helping users feel informed, not judged'
    },
    {
      icon: 'Lock',
      title: 'Trust & Security',
      description: 'Protecting user information at every level'
    }
  ];

  const features = [
    'Track income and expenses in one place',
    'Organize and categorize transactions',
    'Monitor spending patterns over time',
    'Stay aware of upcoming payments and obligations',
    'Gain insights that support better financial awareness'
  ];

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 p-24">
      <h2 className="text-2xl font-heading font-bold text-foreground mb-16">
        About Rovyn Budget
      </h2>

      {/* Hero */}
      <div className="mb-24">
        <p className="text-xl text-muted-foreground font-medium mb-16">
          Take Control of Your Money — With Clarity and Confidence
        </p>
        <p className="text-base text-foreground/90 leading-relaxed mb-12">
          Rovyn Budget is a digital budgeting and financial tracking platform built to help everyday people better understand and manage their money. We believe financial clarity shouldn't be complicated, overwhelming, or reserved for experts — it should be accessible, practical, and empowering.
        </p>
        <p className="text-base text-foreground/90 leading-relaxed">
          Our goal is simple: to give you a clear view of your finances so you can make informed decisions with confidence.
        </p>
      </div>

      {/* Mission */}
      <div className="mb-24">
        <div className="bg-primary/5 rounded-xl p-16 border border-primary/20">
          <div className="flex items-center gap-12 mb-12">
            <div className="flex items-center justify-center w-48 h-48 bg-primary/20 rounded-lg">
              <Icon name="Target" size={24} color="var(--color-primary)" />
            </div>
            <h3 className="text-xl font-heading font-bold">Our Mission</h3>
          </div>
          <p className="text-base text-foreground/90 leading-relaxed mb-12">
            Our mission is to simplify money management and reduce financial stress by giving users the tools they need to see, organize, and plan their finances more effectively. Rovyn Budget is designed to support healthier financial habits through visibility, structure, and thoughtful insights.
          </p>
          <p className="text-lg font-semibold text-primary">
            We focus on clarity — not complexity.
          </p>
        </div>
      </div>

      {/* What We Do */}
      <div className="mb-24">
        <h3 className="text-xl font-heading font-bold mb-12">What Rovyn Budget Does</h3>
        <p className="text-base text-foreground/90 leading-relaxed mb-12">
          Rovyn Budget helps users:
        </p>
        <div className="grid grid-cols-1 gap-12 mb-12">
          {features?.map((feature, index) => (
            <div key={index} className="flex items-start gap-12 bg-muted/50 p-12 rounded-lg">
              <div className="flex-shrink-0 mt-4">
                <Icon name="Check" size={18} color="var(--color-primary)" strokeWidth={3} />
              </div>
              <p className="text-sm text-foreground/90">{feature}</p>
            </div>
          ))}
        </div>
        <p className="text-base text-foreground/90 leading-relaxed">
          Whether you're just starting your budgeting journey or looking for a cleaner way to manage multiple accounts, Rovyn Budget is designed to work around your real-life financial needs.
        </p>
      </div>

      {/* What We Are Not */}
      <div className="mb-24">
        <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-xl p-16">
          <div className="flex items-center gap-12 mb-12">
            <Icon name="AlertCircle" size={24} color="var(--color-warning)" />
            <h3 className="text-xl font-heading font-bold">What We Are Not</h3>
          </div>
          <p className="text-base text-foreground/90 leading-relaxed mb-12">
            Rovyn Budget does not provide financial, investment, tax, or legal advice. All information, tools, and educational content — including blog posts and insights — are provided for general informational and educational purposes only.
          </p>
          <p className="text-base text-foreground/90 leading-relaxed">
            Users remain responsible for their own financial decisions and should consider their personal circumstances or consult qualified professionals when needed.
          </p>
        </div>
      </div>

      {/* Why Created */}
      <div className="mb-24">
        <h3 className="text-xl font-heading font-bold mb-12">Why Rovyn Budget Was Created</h3>
        <p className="text-base text-foreground/90 leading-relaxed mb-12">
          Many people struggle with scattered financial information, unclear spending, and tools that feel more stressful than helpful. Traditional budgeting solutions often rely on rigid systems or complicated spreadsheets that don't reflect how people actually manage money.
        </p>
        <p className="text-base text-foreground/90 leading-relaxed">
          Rovyn Budget was created to be different — simple, flexible, and user-friendly — without sacrificing insight or control.
        </p>
      </div>

      {/* Values */}
      <div className="mb-24">
        <h3 className="text-xl font-heading font-bold mb-12">Our Values</h3>
        <p className="text-base text-foreground/90 leading-relaxed mb-12">
          We are guided by a few core principles:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {values?.map((value, index) => (
            <div key={index} className="bg-muted/50 rounded-lg p-16 border border-border">
              <div className="flex items-center justify-center w-48 h-48 bg-primary/20 rounded-lg mb-12">
                <Icon name={value?.icon} size={24} color="var(--color-primary)" />
              </div>
              <h4 className="text-lg font-heading font-semibold mb-8">{value?.title}</h4>
              <p className="text-sm text-foreground/80">{value?.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Privacy */}
      <div className="mb-24">
        <div className="bg-muted/50 rounded-xl p-16 border border-border">
          <div className="flex items-center gap-12 mb-12">
            <div className="flex items-center justify-center w-48 h-48 bg-primary/20 rounded-lg">
              <Icon name="ShieldCheck" size={24} color="var(--color-primary)" />
            </div>
            <h3 className="text-xl font-heading font-bold">Privacy & Data Protection</h3>
          </div>
          <p className="text-base text-foreground/90 leading-relaxed">
            We take data protection seriously. Rovyn Budget is designed with privacy and security at its core. User data is protected, encrypted, and never sold to third parties. Users remain in control of their information at all times.
          </p>
        </div>
      </div>

      {/* Looking Ahead */}
      <div>
        <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl p-16 border border-primary/20">
          <div className="flex items-center gap-12 mb-12">
            <div className="flex items-center justify-center w-48 h-48 bg-primary/20 rounded-lg">
              <Icon name="Rocket" size={24} color="var(--color-primary)" />
            </div>
            <h3 className="text-xl font-heading font-bold">Looking Ahead</h3>
          </div>
          <p className="text-base text-foreground/90 leading-relaxed mb-12">
            Rovyn Budget is continuously evolving. We are committed to improving the platform, expanding features, and launching a mobile app to make managing your finances even more accessible — wherever life takes you.
          </p>
          <p className="text-lg font-semibold text-primary">
            Built for clarity. Designed for real life.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AboutUsSection;